<template>
    <div class="c-fd" :style="wrapStyle">
        <div :style="innerStyle">
            <!-- ===== Header ===== -->
            <div :style="headerStyle">
                <div :style="topLineStyle">
                    <div :style="eyebrowStyle">{{ agencyName }}</div>
                    <a href="#" @click.prevent="onHystoricalData" :style="hystoricalLinkStyle">
                        Hystorical data ↗
                    </a>
                </div>
                <div :style="completedBadgeWrapStyle">
                    <span :style="completedBadgeStyle">✓</span>
                    <span :style="completedLabelStyle">Review complete</span>
                </div>
                <h1 :style="h1Style">
                    All set. Here's the summary for
                    <span :style="accentStyle">{{ currentMonth }}</span>.
                </h1>
                <p :style="leadStyle">
                    Quick look before you submit — anything off can still be edited.
                </p>
            </div>

            <!-- ===== Per-task cards (sales / invoices / upsells) ===== -->
            <div v-if="summary_data && summary_data.length">
                <div
                    v-for="(task, task_index) in summary_data"
                    :key="`task-${task_index}`"
                >
                    <template v-if="typeof task === 'object' && !Array.isArray(task) && task !== null">
                        <CCard :extra-style="cardStyle">
                            <div :style="cardHeadStyle">
                                <span :style="cardCheckStyle">✓</span>
                                <div :style="cardTitleStyle" v-html="task.title"></div>
                            </div>

                            <!-- Sales table (retainer / project) -->
                            <template v-if="task.type === 'retainer' || task.type === 'project'">
                                <div
                                    v-for="(subtask, index) in Object.values(JSON.parse(task.data))"
                                    :key="`sales-${task_index}-${index}`"
                                    :style="tableWrapStyle"
                                >
                                    <table :style="tableStyle">
                                        <thead>
                                            <tr :style="trHeadStyle">
                                                <th :style="thStyle">Period</th>
                                                <th :style="thStyle">Type</th>
                                                <th :style="thStyleNarrow">Deal</th>
                                                <th :style="thStyleRight">Amount</th>
                                                <th :style="thStyleRight">Collected</th>
                                                <th :style="thStyleRight">Hard&nbsp;cost</th>
                                                <th :style="thStyleAction"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(sale, sale_idx) in subtask"
                                                v-if="typeof sale !== 'string' && subtask !== null"
                                                :key="`sale-${task_index}-${index}-${sale_idx}`"
                                                :style="trBodyStyle(sale_idx)"
                                            >
                                                <td :style="tdStyle">
                                                    {{ getPeriodDate(sale.period || sale.sale.period) }}
                                                </td>
                                                <td :style="tdStyle">{{ sale.type }}</td>
                                                <td
                                                    :title="Object.keys(JSON.parse(task.data))[index]"
                                                    :style="tdStyleDeal"
                                                >
                                                    {{ Object.keys(JSON.parse(task.data))[index] }}
                                                </td>
                                                <td :style="tdStyleNum(isHasChangeValueOfSale(sale.changes, 'Amount'))">
                                                    ${{ getValueFromChangesOfSale(sale.changes, 'Amount') || numberWithCommas(sale.amount) }}
                                                </td>
                                                <td :style="tdStyleNum(isHasChangeValueOfSale(sale.changes, 'Collected'))">
                                                    ${{ getValueFromChangesOfSale(sale.changes, 'Collected') || numberWithCommas(sale.collected) }}
                                                </td>
                                                <td :style="tdStyleNum(isHasChangeValueOfSale(sale.changes, 'Hard Cost'))">
                                                    ${{ getValueFromChangesOfSale(sale.changes, 'Hard Cost') || numberWithCommas(sale.hard_cost) }}
                                                </td>
                                                <td :style="tdStyleAction">
                                                    <a
                                                        href="#"
                                                        :style="editLinkStyle"
                                                        @click.prevent="onEditDealSales(sale.id, sale.sale.deal_id, sale.period || sale.sale.period, sale.sow_index, sale.upsell_index)"
                                                    >Edit</a>
                                                </td>
                                            </tr>
                                            <tr
                                                v-if="Object.keys(subtask).length
                                                    && typeof Object.values(subtask).slice(-1)[0] === 'string'"
                                                :style="trTotalStyle"
                                            >
                                                <td :style="tdTotalLabelStyle" colspan="6">Total commission</td>
                                                <td :style="tdTotalValueStyle">{{ Object.values(subtask).slice(-1)[0] }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>

                            <!-- Invoices table -->
                            <template v-if="task.type === 'invoices'">
                                <div :style="tableWrapStyle">
                                    <table :style="tableStyle">
                                        <thead>
                                            <tr :style="trHeadStyle">
                                                <th :style="thStyle">Period</th>
                                                <th :style="thStyle">Paid&nbsp;date</th>
                                                <th :style="thStyleRight">Paid&nbsp;amount</th>
                                                <th :style="thStyleAction"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(invoice, index) in Object.values(JSON.parse(task.data))"
                                                :key="`inv-${task_index}-${index}`"
                                                :style="trBodyStyle(index)"
                                            >
                                                <td :style="tdStyle">{{ getPeriodDate(invoice.period) }}</td>
                                                <td :style="tdStyleDim(isHasChangeValueOfSale(invoice.changes, 'Payment Received'))">
                                                    {{ getValueFromChangesOfInvoice(invoice.changes, 'Payment Received') }}
                                                </td>
                                                <td :style="tdStyleNum(isHasChangeValueOfSale(invoice.changes, 'Collected'))">
                                                    ${{ getValueFromChangesOfInvoice(invoice.changes, 'Collected') || numberWithCommas(invoice.collected) }}
                                                </td>
                                                <td :style="tdStyleAction">
                                                    <a href="#" :style="editLinkStyle" @click.prevent="onEditInvoices">Edit</a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>

                            <!-- Upsell table -->
                            <template v-if="task.type === 'upsell'">
                                <div :style="tableWrapStyle">
                                    <table :style="tableStyle">
                                        <thead>
                                            <tr :style="trHeadStyle">
                                                <th :style="thStyle">SOW</th>
                                                <th :style="thStyleNarrow">Service</th>
                                                <th :style="thStyle">Type</th>
                                                <th :style="thStyleRight">Amount</th>
                                                <th :style="thStyleAction"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(upsell, index) in Object.values(JSON.parse(task.data))"
                                                :key="`up-${task_index}-${index}`"
                                                :style="trBodyStyle(index)"
                                            >
                                                <td :style="tdStyle">{{ upsell.sow_name }}</td>
                                                <td :style="tdStyleDeal">{{ upsell.service_name }}</td>
                                                <td :style="tdStyle">{{ brand_types[upsell.type_id - 1] }}</td>
                                                <td :style="tdStyleNum(false)">${{ numberWithCommas(upsell.amount) }}</td>
                                                <td :style="tdStyleAction">
                                                    <a
                                                        href="#"
                                                        :style="editLinkStyle"
                                                        @click.prevent="onEditUpsell(upsell.sow_index, upsell.upsell_index)"
                                                    >Edit</a>
                                                    <a
                                                        href="#"
                                                        :style="deleteLinkStyle"
                                                        @click.prevent="onDeleteAction(upsell.id, upsell.sow_name)"
                                                    >Delete</a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </CCard>
                    </template>
                </div>
            </div>

            <!-- ===== Footer / Confirm CTA ===== -->
            <div :style="navStyle">
                <span :style="navHintStyle">Anything off? Edit a row above — otherwise submit.</span>
                <CBtn
                    primary
                    big
                    :disabled="isLoading"
                    :extra-style="confirmBtnStyle"
                    @click="handleConfirmDraft"
                >
                    Submit {{ currentMonth }} →
                </CBtn>
            </div>

            <div class="loader" v-if="isLoading" :style="loaderStyle">
                <clip-loader
                    :loading="isLoading"
                    :color="'#ec4657'"
                    :size="'45px'"
                ></clip-loader>
            </div>

            <confirm-modal
                v-if="isConfirmDeleteModal"
                :title="`Are you sure want to delete upsell ${deleteNameSow}?`"
                :onApplyAction="onDeleteUpsell"
                :onSecondaryAction="onCancelDelete"
                :applyLabel="'Yes'"
                :secondaryLabel="'No'"
            ></confirm-modal>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { SECTIONS } from "../consts";
import ClipLoader from "vue-spinner/src/ClipLoader";
import ConfirmModal from "../../../components/ConfirmModal";
import { scrollToTop } from "../../../helpers/_scrollToTop";

import CCard from "../c-design/CCard.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_BG,
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_GREEN,
    C_PANEL,
    C_RED,
    TAG_RED_DEEP,
    FONT_DISPLAY,
    FONT_BODY,
    navMobileStickyStyle,
} from "../c-design/tokens";

export default {
    name: "Wizard_Final_Dashboard",
    mixins: [viewportMixin],
    props: ["onConfirm", "onChangeSection"],
    components: {
        ClipLoader,
        ConfirmModal,
        CCard,
        CBtn,
    },
    data() {
        return {
            SECTIONS,
            isLoading: false,
            summary_data: [],
            brand_types: ["Monthly", "Full amount", "One-Time Fee"],
            isConfirmDeleteModal: false,
            deleteIdUpsell: null,
            deleteNameSow: "",
        };
    },

    computed: {
        agencyName() {
            return this.$store.getters.getAgencyName;
        },
        currentMonth: () => {
            return moment().format("MMMM'YY");
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '1080px', margin: '0 auto' };
        },
        headerStyle() {
            return {
                marginBottom: this.isMobile ? '16px' : '24px',
                padding: this.isMobile ? '0 4px' : '0 8px',
            };
        },
        topLineStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '14px',
            };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                fontWeight: 600,
            };
        },
        hystoricalLinkStyle() {
            return {
                fontSize: '13px',
                color: C_INK,
                textDecoration: 'none',
                borderBottom: `1px solid ${C_HAIR}`,
            };
        },
        completedBadgeWrapStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '4px',
            };
        },
        completedBadgeStyle() {
            return {
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: C_GREEN,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '17px',
            };
        },
        completedLabelStyle() {
            return {
                fontSize: '12px',
                color: C_GREEN,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontWeight: 600,
            };
        },
        h1Style() {
            return {
                margin: '12px 0 10px',
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '28px' : '40px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.8px' : '-1.2px',
                lineHeight: 1.1,
                color: C_INK,
            };
        },
        accentStyle() {
            return { color: C_RED };
        },
        leadStyle() {
            return {
                margin: 0,
                fontSize: '15px',
                color: C_MUTED,
                lineHeight: 1.55,
                maxWidth: '600px',
            };
        },
        cardStyle() {
            return { marginBottom: '18px', padding: '24px 28px' };
        },
        cardHeadStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px',
            };
        },
        cardCheckStyle() {
            return {
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                background: C_GREEN,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 700,
                flexShrink: 0,
            };
        },
        cardTitleStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '17px',
                fontWeight: 700,
                letterSpacing: '-0.3px',
                color: C_INK,
            };
        },
        tableWrapStyle() {
            return {
                overflowX: 'auto',
                border: `1px solid ${C_HAIR}`,
                borderRadius: '12px',
                marginBottom: '12px',
            };
        },
        tableStyle() {
            return {
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
            };
        },
        trHeadStyle() {
            return { background: C_INK, color: '#fff' };
        },
        thStyle() {
            return {
                padding: '10px 14px',
                textAlign: 'left',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
            };
        },
        thStyleNarrow() {
            return { ...this.thStyle, minWidth: '200px' };
        },
        thStyleRight() {
            return { ...this.thStyle, textAlign: 'right' };
        },
        thStyleAction() {
            return { ...this.thStyle, textAlign: 'right', width: '120px' };
        },
        trTotalStyle() {
            return {
                background: C_CREAM,
                borderTop: `2px solid ${C_HAIR}`,
            };
        },
        tdStyle() {
            return {
                padding: '12px 14px',
                fontSize: '13px',
                color: C_INK2,
                fontFamily: FONT_BODY,
                whiteSpace: 'nowrap',
            };
        },
        tdStyleDeal() {
            return {
                padding: '12px 14px',
                fontSize: '13px',
                color: C_INK,
                fontFamily: FONT_DISPLAY,
                fontWeight: 600,
                maxWidth: '220px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            };
        },
        tdTotalLabelStyle() {
            return {
                padding: '12px 14px',
                fontFamily: FONT_DISPLAY,
                fontSize: '13px',
                fontWeight: 700,
                color: C_INK,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                textAlign: 'right',
            };
        },
        tdTotalValueStyle() {
            return {
                padding: '12px 14px',
                fontFamily: FONT_DISPLAY,
                fontSize: '15px',
                fontWeight: 700,
                color: TAG_RED_DEEP,
                textAlign: 'right',
            };
        },
        editLinkStyle() {
            return {
                fontSize: '12px',
                color: C_RED,
                fontWeight: 600,
                textDecoration: 'none',
                marginLeft: '8px',
                cursor: 'pointer',
            };
        },
        deleteLinkStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                fontWeight: 600,
                textDecoration: 'none',
                marginLeft: '12px',
                cursor: 'pointer',
            };
        },
        navStyle() {
            return navMobileStickyStyle(this.isMobile, {
                marginTop: '8px',
                padding: '20px 28px',
                background: '#fff',
                borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap',
            });
        },
        navHintStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                fontStyle: 'italic',
            };
        },
        confirmBtnStyle() {
            return {
                background: C_RED,
                borderColor: C_RED,
            };
        },
        loaderStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
            };
        },
    },

    mounted() {
        this.getData();
        this.$store.commit("setEditDealId", null);
        this.$store.commit("setSalesPagesIndex", 0);
        this.$store.commit("setIsEditUpsell", false);

        scrollToTop();
    },

    methods: {
        // ===== c-design row & cell style helpers =====
        trBodyStyle(i) {
            return {
                background: i % 2 === 0 ? '#fff' : '#FBF7F0',
                borderTop: `1px solid ${C_HAIR}`,
            };
        },
        tdStyleNum(changed) {
            return {
                padding: '12px 14px',
                fontSize: '13px',
                color: changed ? C_INK : C_MUTED,
                fontFamily: FONT_DISPLAY,
                fontWeight: changed ? 700 : 500,
                textAlign: 'right',
                opacity: changed ? 1 : 0.6,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
            };
        },
        tdStyleDim(changed) {
            return {
                padding: '12px 14px',
                fontSize: '13px',
                color: changed ? C_INK : C_MUTED,
                fontFamily: FONT_BODY,
                opacity: changed ? 1 : 0.6,
                whiteSpace: 'nowrap',
            };
        },

        getPeriodDate(date = null) {
            if (date.includes("/")) return date;

            console.log(date);
            if (date) {
                return moment(date).format("MM/YYYY");
            }
            return "";
        },
        async getData() {
            try {
                this.isLoading = true;

                await axios
                    .post("/api/wizard/getList", { page: "summary" })
                    .then((res) => {
                        const { data } = res;

                        if (data) {
                            this.summary_data = data.page;
                            return;
                        }

                        this.summary_data = null;
                    })
                    .finally(() => {
                        this.isLoading = false;
                        console.log("summary_data", this.summary_data)
                    });

            } catch (err) {
                console.error(err);
            }
        },
        handleConfirmDraft() {
            this.isLoading = true;

            try {
                axios
                    .post("/api/wizard/confirm")
                    .then(() => {
                        //this.$toast.success("Draft confirmed!");
                        this.onConfirm();
                    })
                    .catch((err) => {
                        this.$toast.error(
                            "Something went wrong. Please check error in browser console"
                        );
                        console.error(err);
                    })
                    .finally(() => (this.isLoading = false));
            } catch (err) {
                console.error(err);
            }
        },

        getCollectedAndAmountFromData(arr) {
            // let isCollectedLess = false
            // arr.forEach(item => {
            //     if (item.data.collected < item.data.amount) {
            //         isCollectedLess = true
            //     }
            // })
            // return isCollectedLess
            return arr.some(item => item.data?.collected < item.data?.amount);
        },

        formatRetainerItemsData(items) {
            let str = "";

            // const keys = Object.keys(items);
            // const values = Object.values(items);

            items.forEach((item) => {
                if (item.name === "Invoiced") {
                    str += `Invoiced: $${this.numberWithCommas(item.value)}, `;
                }
                if (item.name === "Collected") {
                    str += `Collected: $${this.numberWithCommas(item.value)}, `;
                }
                if (item.name === "Hard Cost") {
                    str += `Hard cost: $${this.numberWithCommas(item.value)}, `;
                }
                if (item.name === "Payment Received") {
                    str += `Payment Received: ${item.value}, `;
                }
                if (item.name === "Explanation") {
                    str += `Explanation: ${item.value} `;
                }
            });

            // if (keys.length) {
            //     values.forEach((val) => {
            //         if ("amount" in val) {
            //             str += `Invoiced: $${this.numberWithCommas(
            //                 val.amount.value
            //             )}, `;
            //         }
            //         if ("collected" in val) {
            //             str += `Collected: $${this.numberWithCommas(
            //                 val.collected.value
            //             )}, `;
            //         }
            //         if ("hard_cost" in val) {
            //             str += `Hard cost: $${this.numberWithCommas(
            //                 val.hard_cost.value
            //             )} `;
            //         }
            //         if ("explanation" in val) {
            //             str += `Explanation: ${val.explanation.value} `;
            //         }
            //     });
            // }

            return str;
        },

        getValueFromChangesOfSale(changes = null, key = null) {
            if (!changes || !key) {
                return null;
            }

            const changesValues = Object.values(JSON.parse(changes));
            if (changesValues.length > 0 && key) {
                const keyValue = changesValues.find(
                    (item) => item.name === key
                );

                if (keyValue) {
                    return `${this.numberWithCommas(keyValue.value)}`;
                }
                return null;
            }
            return null;
        },

        isHasChangeValueOfSale(changes = null, key = null) {
            if (!changes || !key) {
                return false;
            }

            const changesValues = Object.values(JSON.parse(changes));
            if (changesValues.length > 0 && key) {
                const keyValue = changesValues.find(
                    (item) => item.name === key
                );

                if (keyValue) {
                    return true;
                }
                return false;
            }
            return false;
        },

        getValueFromChangesOfInvoice(changes = null, key = null) {
            if (!changes || !key) {
                return "";
            }
            const changesValues = Object.values(JSON.parse(changes));
            if (changesValues.length > 0 && key) {
                const keyValue = changesValues.find(
                    (item) => item.name === key
                );

                if (key === "Payment Received" && keyValue && keyValue.value) {
                    return keyValue.value;
                }

                if (keyValue && keyValue.value) {
                    return `${this.numberWithCommas(keyValue.value)}`;
                }
                return "";
            }
            return "";
        },

        isHasChangeValueOfInvoice(changes = null, key = null) {
            if (!changes || !key) {
                return false;
            }

            const changesValues = Object.values(JSON.parse(changes));
            if (changesValues.length > 0 && key) {
                const keyValue = changesValues.find(
                    (item) => item.name === key
                );

                if (key === "Payment Received") {
                    return true;
                }

                if (keyValue) {
                    return true;
                }
                return false;
            }
            return false;
        },

        numberWithCommas(x) {
            if (!x) {
                return "0";
            }
            return Number(x)
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },

        onEditDealSales(
            sale_id = null,
            deal_id = null,
            period = null,
            sow_index = null,
            upsell_index = null
        ) {
            console.log("Edit", sale_id, deal_id, period);
            this.$store.commit("setEditDealId", deal_id);

            const sales_deals = this.$store.getters.getAllSalesPages;

            if (sales_deals.length) {
                const deal_index = sales_deals.findIndex(
                    (item) => item.deal_id === deal_id
                );

                if (deal_index !== -1) {
                    let sale_index = sales_deals[deal_index].sales.findIndex(
                        (item) => item.id === sale_id
                    );

                    if (sale_index !== -1) {
                        this.$store.commit("setDealIndex", deal_index);
                        this.$store.commit("setSaleIndex", sale_index);

                        this.onChangeSection(SECTIONS.EDIT_SALE_LINE_ITEM);
                    } else if (sow_index !== null && upsell_index !== null) {
                        this.onEditUpsell(sow_index, upsell_index);
                    } else if (period) {
                        sale_index = sales_deals[deal_index].sales.findIndex(
                            (item) => item.period === period
                        );

                        if (sale_index !== -1) {
                            this.$store.commit("setDealIndex", deal_index);
                            this.$store.commit("setSaleIndex", sale_index);

                            this.onChangeSection(SECTIONS.EDIT_SALE_LINE_ITEM);
                        }
                    }
                }
            }
        },

        onEditInvoices() {
            this.onChangeSection(SECTIONS.INVOICES);
        },

        onViewSalesHistory() {
            this.onChangeSection(
                SECTIONS.EDIT_SALE_LINE_ITEM,
                SECTIONS.FINAL_DASHBOARD
            );
        },

        onEditUpsell(sow_index, upsell_index) {
            this.$store.commit("setIndexSOW", sow_index);
            this.$store.commit("setIndexUpsell", upsell_index);
            this.$store.commit("setIsEditUpsell", true);
            this.onChangeSection(
                SECTIONS.DO_YOU_HAVE_UPSELL,
                SECTIONS.FINAL_DASHBOARD
            );
        },

        onDeleteAction(id, name = "") {
            this.isConfirmDeleteModal = true;
            this.deleteIdUpsell = id;
            this.deleteNameSow = name;
        },

        onDeleteUpsell(id = null) {
            if (!id) {
                return;
            }

            try {
                axios
                    .delete(`/api/wizard/deleteDraft/${this.deleteIdUpsell}`)
                    .then(() => {
                        //this.$toast.success("Upsell draft deleted!");
                        this.isConfirmDeleteModal = false;
                        this.deleteIdUpsell = null;
                        this.deleteNameSow = "";
                        this.getData();
                    })
                    .catch((err) => {
                        console.error(err);
                        this.$toast.error(err.response.data.message);
                    });
            } catch (err) {
                console.error(err);
            }
        },

        onCancelDelete() {
            this.isConfirmDeleteModal = false;
            this.deleteIdUpsell = null;
        },

        onHystoricalData(e) {
            e.preventDefault();
            this.onChangeSection(
                SECTIONS.HYSTORICAL_DATA,
                SECTIONS.FINAL_DASHBOARD
            );
        },
    },
};
</script>
